import { useEffect, useState } from 'react'
import { useAppStore } from '@/stores/appStore'
import { loadTodayAggregates, type Aggregate } from '@/lib/aggregate'
import { InsightWithEvidence } from '@/components/InsightWithEvidence'

export function TodayAggregateInsights({ personId, unborn = false }: { personId: string; unborn?: boolean }) {
  const familyId = useAppStore((s) => s.familyId)
  const userId = useAppStore((s) => s.user?.id)
  const online = useAppStore((s) => s.online)
  const [result, setResult] = useState<{ key: string; values: Record<string, Aggregate>; at: number } | null>(null)
  const [refresh, setRefresh] = useState(0)
  const key = JSON.stringify([familyId, userId, personId, refresh])
  useEffect(() => {
    let active = true
    setResult(null)
    if (!familyId || !userId || !online || unborn) return
    void loadTodayAggregates(familyId, personId).then((values) => {
      if (active) setResult({ key, values, at: Date.now() })
    })
    // Clear before revalidation, on outage, scope changes and when foregrounding.
    const timer = window.setInterval(() => setRefresh((n) => n + 1), 60_000)
    const visible = () => setRefresh((n) => n + 1)
    document.addEventListener('visibilitychange', visible)
    return () => { active = false; window.clearInterval(timer); document.removeEventListener('visibilitychange', visible) }
  }, [familyId, userId, personId, key, online, unborn])
  if (!familyId || !userId) return null
  if (unborn) return <p className="text-caption text-text-secondary">Kick evidence unavailable: movements on this device have no server event IDs.</p>
  const values = online && result?.key === key && Date.now() - result.at < 60_000 ? result.values : null
  return <section aria-label="Evidence-backed totals" className="space-y-2">
    <h2 className="text-title">Logged in the last day</h2>
    <p className="text-caption text-text-secondary">Server record only; not a complete account of care.</p>
    {!values ? <p role="status">{online ? 'Checking verified records…' : 'Offline: verified totals unavailable.'}</p> : Object.entries(values).map(([metric, a]) =>
      a.status === 'known' ? <InsightWithEvidence key={metric} number={a.number} unit={a.unit} label="logged in the last day" evidence={a.evidence} />
        : <p key={metric} className="text-caption text-text-secondary">{metric}: unknown — {a.reason}</p>)}
    <button type="button" onClick={() => setRefresh((n) => n + 1)} className="min-h-11 underline">Refresh evidence</button>
  </section>
}
